import type { CaptureResult, CapturedNetworkRequest } from 'posthog-js';

import { maskAttributeFn, maskTextFn, maskValue } from './replayMasking';

// Auth pages carry addresses and a temporary password in the query, and list tables put the
// free-text search and filter values (which can hold client names) into `search`/`qf`/`mf`.
// Global search puts its own query in `q`, which is the whole of the results page's state: the
// search events themselves send only its length, and the URL must not send what they withheld.
// The same list covers the backend requests the recorder captures: the lists send their state as
// an RSQL `filter` (`filters` for the marketing-profile preview), the task and notification lists
// as `searchText`, help-center search as `query`, and the party duplicate check sends the name or
// identifier typed into the contact form as `searchTerm`.
const PERSONAL_QUERY_PARAMS = [
    'email',
    'tempPassword',
    'to',
    'search',
    'qf',
    'mf',
    'q',
    'filter',
    'filters',
    'searchText',
    'searchTerm',
    'query',
];

// Rebuilt entry by entry so a repeated key keeps every occurrence, each masked on its own.
const maskValues = (params: URLSearchParams, isPersonal: (key: string) => boolean): string =>
    new URLSearchParams(
        Array.from(params.entries(), ([key, value]) => [key, isPersonal(key) ? maskValue(value) : value])
    ).toString();

// Every fragment value is masked: the only hashes this app produces are OAuth responses
// (`/login#code=…&id_token=…`), and the id_token JWT embeds the user's name and e-mail.
const maskFragment = (hash: string): string => {
    const fragment = hash.replace(/^#/, '');
    if (!fragment) {
        return hash;
    }
    if (!fragment.includes('=')) {
        return `#${maskValue(fragment)}`;
    }

    return `#${maskValues(new URLSearchParams(fragment), () => true)}`;
};

// Values are starred out rather than dropped, the way the recorder masks text, so the URL keeps its
// shape: a masked `q` still says a search happened and how long the term was.
const maskPersonalParts = (url: URL): URL => {
    url.search = maskValues(url.searchParams, (key) => PERSONAL_QUERY_PARAMS.includes(key));
    url.hash = maskFragment(url.hash);

    return url;
};

const scrubUrl = (value: string): string => {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        // Non-URL markers such as the '$direct' referrer.
        return value;
    }

    return maskPersonalParts(url).toString();
};

// The recorder's rule for a link target applies to autocapture too: an external one (`mailto:`
// addresses, document links) is starred out, a same-origin one is kept with the same parts masked
// as in the page URL. A relative target stays relative so the chain reads as the page rendered it.
const scrubHref = (value: string): string => {
    const masked = maskAttributeFn('href', value);
    if (masked !== value) {
        return masked;
    }
    if (value.startsWith('#')) {
        return maskFragment(value);
    }
    if (!value.includes('?') && !value.includes('#')) {
        return value;
    }

    const url = maskPersonalParts(new URL(value, window.location.origin));

    return value.startsWith(url.origin) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
};

// Edit pages put the record's name into document.title, so each segment must be verbatim app
// copy to be transmitted. The timecard timer prefixes the title with a plain duration.
const TIMER_SEGMENT = /^[\d:]+$/;

const scrubTitle = (title: string): string =>
    title
        .split(' - ')
        .map((segment) => (TIMER_SEGMENT.test(segment) ? segment : maskTextFn(segment)))
        .join(' - ');

const WORD_CHARACTER = String.raw`[\p{L}\p{N}]`;
const EMAIL_CHARACTER = String.raw`[\p{L}\p{N}._%+'-]`;

const QUOTE_PAIRS: [string, string][] = [
    ['"', '"'],
    ['`', '`'],
    ['“', '”'],
    ['‘', '’'],
    ["'", "'"],
];
const CLOSING_QUOTE: Record<string, string> = Object.fromEntries(QUOTE_PAIRS);

// A straight `'` right after a letter or digit is an apostrophe (`can't`) and opens nothing.
const opening = (open: string): string => (open === "'" ? `(?<!${WORD_CHARACTER})'` : open);

// From an opening quote to its closing one, or to the end of the message when it never closes. A
// backslash-escaped quote, and one followed by a letter or digit, is part of the value: an
// apostrophe (`'O'Brien'`), or a nested quote Chrome's JSON error leaves unescaped.
const quoted = ([open, close]: [string, string]): string =>
    String.raw`${opening(open)}(?:\\.|[^${close}]|${close}(?=${WORD_CHARACTER}))*(?:${close}|$)`;

// One pass over the raw message, so the apostrophe test sees the original letters: starring the
// address in `jane@x.com's` first would put a star before the quote and open a span there. An
// address starts where its run of characters starts, which keeps a long run with no `@` linear,
// and never on a `'`, which opens the quoted span around it instead.
const MESSAGE_DATA = new RegExp(
    [
        String.raw`(?<!${EMAIL_CHARACTER})(?!')${EMAIL_CHARACTER}+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+`,
        String.raw`\d{4,}`,
        ...QUOTE_PAIRS.map(quoted),
    ].join('|'),
    'gu'
);

// The quotes themselves stay, so the message keeps its shape.
const maskMessageData = (span: string): string => {
    const close = CLOSING_QUOTE[span[0]];
    if (!close) {
        return maskValue(span);
    }
    const end = span.length > 1 && span.endsWith(close) ? -1 : span.length;

    return span[0] + maskValue(span.slice(1, end)) + span.slice(end);
};

// Exception messages keep their words and lose what can carry record data: backend error text and
// stringified rejection payloads quote values, ids and e-mail addresses. The type and stack frames stay.
const scrubExceptionList = (value: unknown): unknown => {
    if (!Array.isArray(value)) {
        return value;
    }

    return value.map((item) =>
        isPlainObject(item) && typeof item.value === 'string'
            ? { ...item, value: item.value.replace(MESSAGE_DATA, maskMessageData) }
            : item
    );
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// Matched by key so every URL-bearing property is covered wherever it rides: $current_url and
// $referrer on events, the $initial_* pair inside $set_once, $session_entry_url, web vitals.
const URL_PROPERTY_KEY = /url|referrer/i;

/*
 * Autocapture sends the clicked element's own text, and a search row, a list cell or a breadcrumb
 * draws a record's name. It arrives twice in the same event: as `$el_text` — on the event itself and
 * again on every entry of `$elements` — and flattened into `$elements_chain` as a `text="..."` run.
 * Masking one of the two would leave the other carrying the name in full, so both go through the
 * allowlist the recorder already masks by: the app's own translated copy and the tenant's classifier
 * labels are transmitted, and everything else is starred out.
 *
 * Masking here rather than by `mask_all_text` because that setting takes the button labels with it,
 * and the labels are what makes an autocapture event worth having. Of the element attributes only
 * the link target is handled here; the rest have their own levers (`mask_all_element_attributes`,
 * `element_attribute_ignorelist`).
 */
const ELEMENT_TEXT_KEY = '$el_text';
const ELEMENTS_KEY = '$elements';
const ELEMENTS_CHAIN_KEY = '$elements_chain';

// A link's target rides along with its text: as `attr__href` on the `$elements` entry, in the chain
// twice, as `href` and again among the `attr__*` attributes, and, for an external one, a third time
// on the event itself.
const ELEMENT_HREF_KEY = /^(attr__)?href$/;
const EXTERNAL_CLICK_URL_KEY = '$external_click_url';

const scrubElement = (element: Record<string, unknown>): void => {
    Object.entries(element).forEach(([key, value]) => {
        if (ELEMENT_HREF_KEY.test(key) && typeof value === 'string') {
            element[key] = scrubHref(value);
        } else if (key === ELEMENT_TEXT_KEY && typeof value === 'string') {
            element[key] = maskTextFn(value);
        } else if (isPlainObject(value)) {
            scrubElement(value);
        }
    });
};

const scrubElements = (elements: unknown[]): void => {
    elements.forEach((element) => {
        if (isPlainObject(element)) {
            scrubElement(element);
        }
    });
};

// PostHog escapes a chain value's double quotes and leaves every other character alone, so a run
// ends at the first quote that is not preceded by a backslash and the inverse is that one pair.
// Every run is consumed in turn, so a name is always read from where the previous run ended and an
// attribute merely ending in "text" is not mistaken for it.
const CHAIN_ATTRIBUTE_RUN = /([\w-]+)="((?:\\.|[^"\\])*)"/g;

const scrubChainValue = (name: string, value: string): string | undefined => {
    if (name === 'text') {
        return maskTextFn(value);
    }
    if (ELEMENT_HREF_KEY.test(name)) {
        return scrubHref(value);
    }
    return undefined;
};

const scrubElementsChain = (chain: string): string =>
    chain.replace(CHAIN_ATTRIBUTE_RUN, (run: string, name: string, escaped: string) => {
        const scrubbed = scrubChainValue(name, escaped.replace(/\\"/g, '"'));
        return scrubbed === undefined ? run : `${name}="${scrubbed.replace(/"/g, '\\"')}"`;
    });

const scrubProperties = (properties: Record<string, unknown>): void => {
    Object.entries(properties).forEach(([key, value]) => {
        if (key === '$exception_list') {
            properties[key] = scrubExceptionList(value);
        } else if (key === 'title' && typeof value === 'string') {
            properties[key] = scrubTitle(value);
        } else if (key === ELEMENT_TEXT_KEY && typeof value === 'string') {
            properties[key] = maskTextFn(value);
        } else if (key === ELEMENTS_KEY && Array.isArray(value)) {
            scrubElements(value);
        } else if (key === ELEMENTS_CHAIN_KEY && typeof value === 'string') {
            properties[key] = scrubElementsChain(value);
        } else if (key === EXTERNAL_CLICK_URL_KEY && typeof value === 'string') {
            properties[key] = scrubHref(value);
        } else if (URL_PROPERTY_KEY.test(key) && typeof value === 'string') {
            properties[key] = scrubUrl(value);
        } else if (Array.isArray(value)) {
            scrubArray(value);
        } else if (isPlainObject(value)) {
            scrubProperties(value);
        }
    });
};

// Bare strings inside arrays carry no key to match on, so only records are descended into.
const scrubArray = (values: unknown[]): void => {
    values.forEach((item) => {
        if (Array.isArray(item)) {
            scrubArray(item);
        } else if (isPlainObject(item)) {
            scrubProperties(item);
        }
    });
};

export const scrubPersonalData = (event: CaptureResult | null): CaptureResult | null => {
    if (!event) {
        return event;
    }

    scrubProperties(event.properties);
    if (event.$set) {
        scrubProperties(event.$set);
    }
    if (event.$set_once) {
        scrubProperties(event.$set_once);
    }

    return event;
};

// The browser reports a resize observer that changed layout again within the same frame; the
// pending observations are delivered on the next one. Older Chrome words it "loop limit exceeded".
const RESIZE_OBSERVER_LOOP = /^ResizeObserver loop/;

export const dropResizeObserverNoise = (event: CaptureResult | null): CaptureResult | null => {
    const exceptions: unknown = event?.event === '$exception' ? event.properties.$exception_list : undefined;
    const message = Array.isArray(exceptions) && isPlainObject(exceptions[0]) ? exceptions[0].value : undefined;

    return typeof message === 'string' && RESIZE_OBSERVER_LOOP.test(message) ? null : event;
};

// The recorder keeps every request's URL (headers and bodies stay off), and the backend takes the
// list state as GET parameters, so a request line carries what the page URL has just masked.
export const maskCapturedNetworkRequest = (request: CapturedNetworkRequest): CapturedNetworkRequest => ({
    ...request,
    name: scrubUrl(request.name),
});
